import React, { useEffect, useState } from "react";
import "./index.less";
import { ChatLayoutContainer } from "../ChatLayoutContainer";
import { useParams } from "react-router-dom";
import { ChatMsg, ChatPrivateTop, ChatSendInput, ContactItem, IMsgItem } from "remo-components";
import TencentCloudChat from "@tencentcloud/chat";
import { useTranslate } from "@obsbot/remo-i18n";
import { RouteNameType, usePushHistory } from "../../utils/routes";
import { useFriendStore, useGlobalStore } from "@obsbot/remo-stores";
import { v4 as uuidv4 } from "uuid";
import { CustomType } from "@obsbot/remo-services";
import { eventBus } from "@obsbot/remo-utils";
import { setIntimateChatRead } from "@obsbot/remo-server-api";

const cacheMap = new Map<string, IMsgItem[]>();
const TextChatPage = (): React.ReactElement => {
    const params = useParams();
    const t = useTranslate();
    const pushHistory = usePushHistory();
    const { conversation, rtm, intimate } = useFriendStore();
    const { userInfo } = useGlobalStore();
    const { conversationList = [] } = conversation;
    const [curConversation, setCurConversation] = useState<ContactItem>();
    const [referenceMsg, setReferenceMessage] = useState<IMsgItem | null>(null);
    const [tempMsg, setTempMsg] = useState<any>(null);

    useEffect(() => {
        const C2C_List =
            conversationList &&
            conversationList.filter(item => {
                return item.type === TencentCloudChat.TYPES.CONV_C2C;
            });
        if (C2C_List.length === 0) {
            pushHistory(RouteNameType.AddFriendsPage);
        } else {
            const { id } = params;
            if (id && !C2C_List.find(item => item.userProfile.userID === id)) {
                const { userProfile } = C2C_List[0];
                pushHistory(RouteNameType.TextChatPage, {
                    id: userProfile.userID,
                    name: userProfile.nick || userProfile.userID,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationList]);

    useEffect(() => {
        if (params.id && rtm) {
            const tempConversationID = "C2C" + params.id;
            const tempConversation = conversationList.find(
                item => item.conversationID === tempConversationID,
            );
            setCurConversation(tempConversation);
            rtm.setMessageRead(tempConversationID); // 对单聊内所有消息已读，sdk数据
        }
        // 对单聊消息内的亲密关系邀请已读（后台数据）
        if (params.id && intimate.unreadCount > 0) {
            const sendsUser = intimate.receiveList.find(item => item.from.userID === params.id);
            if (sendsUser) {
                setIntimateChatRead(params.id);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id]);

    // 监听事件
    useEffect(() => {
        const handleSendMsg = ({
            userID,
            conversationID,
        }: {
            userID: string;
            conversationID?: string;
        }): void => {
            conversationID = conversationID || "C2C" + userID;
            if (cacheMap.has(conversationID)) {
                cacheMap.delete(conversationID);
            }
        };
        eventBus.on("send-new-msg", handleSendMsg);
        return () => {
            eventBus.off("send-new-msg", handleSendMsg);
        };
    }, []);

    const handleSendMessage = (_message: string, _type: CustomType, _success: boolean): void => {
        setTempMsg(null);
        if (params.id) {
            eventBus.emit("send-new-msg", {
                userID: params.id,
            });
        }
    };

    // 设置文件上传时的占位消息
    const handleTempMsg = (file: File, type: CustomType): void => {
        setTempMsg({
            sequence: Math.floor(Math.random() * 100000),
            type: TypeReflect[type as keyof typeof TypeReflect],
            conversationType: TencentCloudChat.TYPES.CONV_C2C,
            conversationID: curConversation?.conversationID || "",
            ID: uuidv4(),
            flow: "out",
            from: userInfo?.user_id || "",
            to: params.id || "",
            avatar: userInfo?.avatar || "",
            nick: userInfo?.nickname || "",
            payload: {
                file: file,
            },
            status: "sending",
        });
    };

    return (
        <ChatLayoutContainer>
            <div className="text-chat-page">
                <ChatPrivateTop
                    cacheMap={cacheMap}
                    chatUser={curConversation}
                    title={params?.name || t("chat.unknown-window")}
                />
                {curConversation?.conversationID && (
                    <ChatMsg
                        conversationID={curConversation.conversationID}
                        count={10}
                        referenceMessage={referenceMsg}
                        setReferenceMessage={setReferenceMessage}
                        tempMsg={tempMsg}
                    />
                )}
                <ChatSendInput
                    handleSendMessage={handleSendMessage}
                    handleTempMsg={handleTempMsg}
                    hoverWindow={false}
                    referenceMessage={referenceMsg}
                    setReferenceMessage={setReferenceMessage}
                />
            </div>
        </ChatLayoutContainer>
    );
};

export default TextChatPage;

const TypeReflect = {
    [CustomType.Text]: TencentCloudChat.TYPES.MSG_TEXT,
    [CustomType.Image]: TencentCloudChat.TYPES.MSG_IMAGE,
    [CustomType.Video]: TencentCloudChat.TYPES.MSG_VIDEO,
    [CustomType.Audio]: TencentCloudChat.TYPES.MSG_AUDIO,
    [CustomType.File]: TencentCloudChat.TYPES.MSG_FILE,
    [CustomType.Custom]: TencentCloudChat.TYPES.MSG_CUSTOM,
};
